import React from 'react';
import classes from './FormQuestions.module.scss';
import { Checkbox } from 'components/Form/Checkbox/Checkbox';
import { Input } from 'components/Form/Input/Input';
import { RadioGroup } from 'components/Form/RadioGroup/RadioGroup';
import { FormQuestion } from 'types/FormsInvitation';

type AnswerValue = string | string[];

interface FormQuestionsProps {
    questions: FormQuestion[];
    formId: number;
    answers: { [questionId: number]: AnswerValue };
    onSave: (formId: number, answers: { [questionId: number]: AnswerValue }) => void;
}

const isMultipleChoice = (t?: string) => t === 'multiple_choice';
const isSingleChoice = (t?: string) => t === 'single_choice';
const isLong = (t?: string) => t === 'long_answer';
const isShort = (t?: string) => t === 'short_answer';

const normalizeAnswerByType = (
    answerType: FormQuestion['answerType'],
    raw?: string | string[] | null
): AnswerValue => {
    if (answerType === 'multiple_choice') {
        return Array.isArray(raw) ? raw : [];
    }

    return typeof raw === 'string' ? raw : '';
};

export const FormQuestions: React.FC<FormQuestionsProps> = props => {
    const [localAnswers, setLocalAnswers] = React.useState<{ [questionId: number]: AnswerValue }>(
        props.answers || {}
    );

    const questionIdsKey = React.useMemo(
        () => props.questions.map(q => q.id).join(','),
        [props.questions]
    );

    React.useEffect(() => {
        const normalized: { [questionId: number]: AnswerValue } = {};

        props.questions.forEach(q => {
            normalized[q.id] = normalizeAnswerByType(q.answerType, props.answers?.[q.id] ?? null);
        });

        setLocalAnswers(prev => {
            const prevStr = JSON.stringify(prev);
            const nextStr = JSON.stringify(normalized);
            return prevStr === nextStr ? prev : normalized;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.formId, props.answers, questionIdsKey]);

    const handleInputChange = (questionId: number, value: string) => {
        setLocalAnswers(prev => ({ ...prev, [questionId]: value }));
    };

    const handleInputBlur = () => {
        setLocalAnswers(prev => {
            props.onSave(props.formId, prev);
            return prev;
        });
    };

    const handleRadioChange = (questionId: number, value: string) => {
        setLocalAnswers(prev => {
            const newAnswers = { ...prev, [questionId]: value };
            props.onSave(props.formId, newAnswers);
            return newAnswers;
        });
    };

    const handleCheckboxChange = (questionId: number, optionValue: string, checked: boolean) => {
        setLocalAnswers(prev => {
            const currentValues = Array.isArray(prev[questionId])
                ? (prev[questionId] as string[])
                : [];
            const newValues = checked
                ? Array.from(new Set([...currentValues, optionValue]))
                : currentValues.filter(v => v !== optionValue);

            const newAnswers = { ...prev, [questionId]: newValues };
            props.onSave(props.formId, newAnswers);
            return newAnswers;
        });
    };

    const getActiveOptionsSorted = (q: FormQuestion) => {
        const opts = q.options || [];
        return opts
            .filter(o => o.active !== false)
            .slice()
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    };

    const renderQuestion = (question: FormQuestion) => {
        const answerType = question.answerType;
        const value = localAnswers[question.id];

        // ✅ Short answer
        if (isShort(answerType)) {
            return (
                <div key={question.id} className={classes.inputGroup}>
                    <label className={classes.label}>
                        {question.question}
                        {question.required && <span className={classes.asterisk}>*</span>}
                    </label>

                    <Input
                        value={typeof value === 'string' ? value : ''}
                        onChange={e => handleInputChange(question.id, e.target.value)}
                        onBlur={handleInputBlur}
                        placeholder="Write your answer"
                        className={classes.inputText}
                        inputProps={{ maxLength: '250' }}
                        fullWidth
                    />
                </div>
            );
        }

        // ✅ Long answer
        if (isLong(answerType)) {
            return (
                <Input
                    key={question.id}
                    label={question.question}
                    required={question.required}
                    multiline
                    rows={4}
                    value={typeof value === 'string' ? value : ''}
                    onChange={e => handleInputChange(question.id, e.target.value)}
                    onBlur={handleInputBlur}
                    placeholder="Write your answer"
                    className={classes.textarea}
                    fullWidth
                    inputProps={{ maxLength: '1000' }}
                />
            );
        }

        // ✅ Single choice
        if (isSingleChoice(answerType)) {
            const options = getActiveOptionsSorted(question);

            const radioOptions = options.map(opt => ({
                id: opt.value,
                name: opt.label
            }));

            return (
                <div key={question.id} className={classes.radioGroup}>
                    <label className={`${classes.label}`}>
                        {question.question}
                        {question.required && <span className={classes.asterisk}>*</span>}
                    </label>
                    <div className={classes.radioOptions}>
                        <RadioGroup
                            options={radioOptions}
                            value={typeof value === 'string' ? value : ''}
                            onChange={opt => handleRadioChange(question.id, opt.id)}
                        />
                    </div>
                </div>
            );
        }

        // ✅ Multiple choice
        if (isMultipleChoice(answerType)) {
            const options = getActiveOptionsSorted(question);
            const selected = Array.isArray(value) ? value : [];

            return (
                <div key={question.id} className={classes.checkboxGroup}>
                    <label className={`${classes.label} ${classes.checkboxLabel}`}>
                        {question.question}
                        {question.required && <span className={classes.asterisk}>*</span>}
                    </label>

                    <div className={classes.checkboxOptions}>
                        {options.map(opt => (
                            <Checkbox
                                key={opt.id ?? opt.value}
                                label={opt.label}
                                checked={selected.includes(opt.value)}
                                onChange={e =>
                                    handleCheckboxChange(question.id, opt.value, e.target.checked)
                                }
                            />
                        ))}
                    </div>
                </div>
            );
        }

        // Fallback (por si llega algo inesperado)
        return (
            <Input
                key={question.id}
                label={question.question}
                required={question.required}
                value={typeof value === 'string' ? value : ''}
                onChange={e => handleInputChange(question.id, e.target.value)}
                onBlur={handleInputBlur}
                placeholder="Write your answer"
                fullWidth
            />
        );
    };

    return (
        <div className={classes.root}>
            <div className={classes.questions}>{props.questions.map(q => renderQuestion(q))}</div>
            <div className={classes.actions} />
        </div>
    );
};
